import { Box, Typography } from '@mui/material';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { TruckRecordingMip } from '../../api/queries/truckRecordingMipList';
import { getProbabilityColor } from '../Prediction/utils';
import { getTruckRecordingMaterialPrediction } from './truckRecordingMaterialPrediction';

interface TruckRecordingMaterialPredictionCellProps {
  recording: TruckRecordingMip;
}

export function TruckRecordingMaterialPredictionCell({
  recording,
}: TruckRecordingMaterialPredictionCellProps): ReactElement {
  const { t } = useTranslation();
  const prediction = getTruckRecordingMaterialPrediction(recording);

  if (!prediction) {
    return (
      <Box component='span' display='flex' alignItems='center' alignSelf='stretch' height='100%' width='100%'>
        <Box component='span' sx={visuallyHiddenSx}>
          {t('recording.materialPredictionUnavailable')}
        </Box>
      </Box>
    );
  }

  const materialLabel =
    prediction.confidence === 'low'
      ? t('recording.materialPredictionUncertain')
      : t(`material.class.${prediction.className}`);
  const confidenceLabel = t(`recording.materialPredictionConfidence.${prediction.confidence}`);

  return (
    <Box
      component='span'
      display='flex'
      alignItems='center'
      alignSelf='stretch'
      height='100%'
      minWidth={0}
      width='100%'
    >
      <Box
        component='span'
        sx={{
          alignItems: 'stretch',
          backgroundColor: 'white',
          border: '1px solid rgba(0, 0, 0, 0.12)',
          borderRadius: '6px',
          boxSizing: 'border-box',
          display: 'flex',
          flex: 1,
          minHeight: '2rem',
          minWidth: 0,
        }}
      >
        <Box component='span' display='flex' alignItems='center' minWidth={0} flex={1} sx={{ padding: '4px 8px' }}>
          <Typography component='span' noWrap textOverflow='ellipsis' overflow='hidden'>
            {materialLabel}
          </Typography>
          <Box component='span' sx={visuallyHiddenSx}>
            {confidenceLabel}
          </Box>
        </Box>
        <Box
          component='span'
          aria-hidden='true'
          sx={{
            alignSelf: 'stretch',
            backgroundColor: getProbabilityColor(prediction.confidence),
            borderRadius: '0 6px 6px 0',
            flexShrink: 0,
            width: '3px',
          }}
        />
      </Box>
    </Box>
  );
}

const visuallyHiddenSx = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px',
};
